import { useAuth } from "@dl/web-core-js/auth";
import { Button, Dialog, entityOptions, MultiSelect } from "@dl/web-core-js/ui";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ProductClient, type LazyProductDTO, type NamedDTO } from "../../api/product";

export type TagBulkMode = "add" | "remove";

/**
 * Adds tags to, or strips tags from, the products selected in the grid.
 *
 * One dialog for both directions because they are the same gesture — pick
 * tags, apply to the selection — and the server makes both idempotent per
 * product: a product that already carries the tag, or never had it, is left
 * alone. The line under the picker says how many products will actually
 * change, computed from the rows on screen, so "12 selected" does not turn
 * into a surprise when only 3 were missing the tag.
 *
 * In remove mode the picker offers only tags the selection carries. Offering
 * the whole catalogue there would be a list of choices that do nothing.
 */
export function TagBulkDialog({
  open,
  mode,
  products,
  tags,
  onClose,
  onDone,
}: {
  open: boolean;
  mode: TagBulkMode;
  /** The grid's selection, with their current tags. */
  products: LazyProductDTO[];
  /** Every tag there is. */
  tags: NamedDTO[];
  onClose: () => void;
  /** After a successful write, so the grid can reload. */
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new ProductClient(http), [http]);
  const [picked, setPicked] = useState<string[]>([]);

  // A fresh pick every time the dialog opens; a stale one from the last
  // operation applied to a new selection is exactly the mistake to prevent.
  useEffect(() => {
    if (open) setPicked([]);
  }, [open, mode]);

  const carried = useMemo(() => {
    const byTag = new Map<number, number>();
    for (const product of products) {
      for (const tag of product.tags ?? []) {
        byTag.set(tag.id, (byTag.get(tag.id) ?? 0) + 1);
      }
    }
    return byTag;
  }, [products]);

  const options = useMemo(() => {
    const pool = mode === "remove" ? tags.filter((tag) => carried.has(tag.id)) : tags;
    return entityOptions(pool);
  }, [mode, tags, carried]);

  // Products that would actually change: missing at least one picked tag
  // when adding, carrying at least one when removing.
  const affected = useMemo(() => {
    if (picked.length === 0) return 0;
    const ids = new Set(picked.map(Number));
    return products.filter((product) => {
      const own = new Set((product.tags ?? []).map((tag) => tag.id));
      return mode === "add" ? [...ids].some((id) => !own.has(id)) : [...ids].some((id) => own.has(id));
    }).length;
  }, [picked, products, mode]);

  const apply = useMutation({
    mutationFn: () => {
      const productIds = products.map((product) => product.id);
      const tagIds = picked.map(Number);
      return mode === "add" ? client.addTags(productIds, tagIds) : client.removeTags(productIds, tagIds);
    },
    onSuccess: (changed) => {
      toast.success(
        mode === "add"
          ? t("products.tags.added", "Tagged {{count}} products", { count: changed.length })
          : t("products.tags.removed", "Untagged {{count}} products", { count: changed.length }),
      );
      onDone();
      onClose();
    },
    onError: (failure) =>
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Request failed")),
  });

  const title =
    mode === "add"
      ? t("products.tags.add.title", "Add tags to {{count}} products", { count: products.length })
      : t("products.tags.remove.title", "Remove tags from {{count}} products", { count: products.length });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={
        mode === "add"
          ? t("products.tags.add.description", "Every selected product gets every tag picked here. A product that already carries one is left as it is.")
          : t("products.tags.remove.description", "Every tag picked here is taken off every selected product. A product without it is left as it is.")
      }
      size="md"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={apply.isPending}>
            {t("button.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={() => apply.mutate()}
            disabled={picked.length === 0 || affected === 0}
            loading={apply.isPending}
          >
            {mode === "add" ? t("products.tags.add.confirm", "Add tags") : t("products.tags.remove.confirm", "Remove tags")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <MultiSelect
          options={options}
          value={picked}
          onChange={setPicked}
          placeholder={t("products.tags.pick", "Pick tags")}
          searchThreshold={8}
        />
        {options.length === 0 ? (
          <p className="text-[12px] text-ink-muted">
            {t("products.tags.none-to-remove", "None of the selected products carries a tag.")}
          </p>
        ) : picked.length > 0 ? (
          <p className="text-[12px] text-ink-muted">
            {affected === 0
              ? mode === "add"
                ? t("products.tags.affected.none-add", "Every selected product already has these tags: nothing to do.")
                : t("products.tags.affected.none-remove", "None of the selected products has these tags: nothing to do.")
              : t("products.tags.affected", "{{affected}} of {{total}} selected products will change.", {
                  affected,
                  total: products.length,
                })}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
